'use client'

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { useGetTranslations } from '@/root/shared-hooks'
import { News } from '@/root/shared-types'
import { Notification } from '@/root/modules/tuuleliinid/'
import { ChevronLeftIcon } from '@/root/ui/shared/icons/ChevronLeftIcon'

interface NotificationsProps {
  news: News[]
  headingValue?: string
}

const PAGE_SIZE = 4
const HERO_NEWS_EVENT = 'HERO_NEWS_SELECT'

const smoothScrollTo = (elementId: string) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const el = document.getElementById(elementId)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    })
  })
}

export const Notifications: FC<NotificationsProps> = ({ news, headingValue }) => {
  const { getTranslation } = useGetTranslations()
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const pendingScrollIdRef = useRef<string | null>(null)
  const isInitialScrollDone = useRef(false)

  const title = headingValue || getTranslation('newsPageTitle')

  const sortedNews = useMemo(() => {
    return (news ?? []).slice().toSorted((a, b) => {
      const db = new Date(b.published_at).getTime()
      const da = new Date(a.published_at).getTime()
      if (db !== da) return db - da
      return Number(b.id) - Number(a.id)
    })
  }, [news])

  const initialPage = useMemo(() => {
    const newsId = searchParams?.get('newsId')
    if (newsId && sortedNews.length > 0) {
      const idx = sortedNews.findIndex((n) => String(n.id) === String(newsId))
      if (idx >= 0) return Math.floor(idx / PAGE_SIZE)
    }

    const pageParam = searchParams?.get('page')
    return pageParam ? Math.max(0, Number(pageParam) - 1) : 0
  }, [searchParams, sortedNews])

  const [page, setPage] = useState(initialPage)

  const totalPages = Math.max(1, Math.ceil(sortedNews.length / PAGE_SIZE))

  const paginationDots = useMemo(() => {
    return Array.from({ length: totalPages }).map((_, i) => ({
      id: `pagination-dot-${i}`,
      pageIndex: i,
    }))
  }, [totalPages])

  const pageNews = useMemo(() => {
    const start = page * PAGE_SIZE
    return sortedNews.slice(start, start + PAGE_SIZE)
  }, [sortedNews, page])

  const changePage = useCallback(
    (newPage: number) => {
      setPage(newPage)
      pendingScrollIdRef.current = null

      const params = new URLSearchParams(searchParams?.toString())
      params.set('page', String(newPage + 1))
      params.delete('newsId')

      router.push(`${pathname}?${params.toString()}`, { scroll: false })

      smoothScrollTo('notifications')
    },
    [pathname, router, searchParams]
  )

  const goPrev = () => changePage(Math.max(0, page - 1))
  const goNext = () => changePage(Math.min(totalPages - 1, page + 1))

  const scrollToNewsId = useCallback(
    (newsId: string) => {
      const idx = sortedNews.findIndex((n) => String(n.id) === String(newsId))
      if (idx < 0) return

      const targetPage = Math.floor(idx / PAGE_SIZE)

      const params = new URLSearchParams(searchParams?.toString())
      params.set('page', String(targetPage + 1))
      params.set('newsId', String(newsId))

      router.replace(`${pathname}?${params.toString()}`, { scroll: false })

      if (page === targetPage) {
        smoothScrollTo(`news-${newsId}`)
      } else {
        pendingScrollIdRef.current = String(newsId)
        setPage(targetPage)
      }
    },
    [sortedNews, page, pathname, router, searchParams]
  )

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ id: string }>
      if (ce.detail?.id) scrollToNewsId(ce.detail.id)
    }
    window.addEventListener(HERO_NEWS_EVENT, handler)
    return () => window.removeEventListener(HERO_NEWS_EVENT, handler)
  }, [scrollToNewsId])

  useEffect(() => {
    const id = searchParams?.get('newsId')

    if (id && sortedNews.length > 0 && !isInitialScrollDone.current) {
      scrollToNewsId(id)
      isInitialScrollDone.current = true
    }
  }, [searchParams, sortedNews.length, scrollToNewsId])

  useEffect(() => {
    const id = pendingScrollIdRef.current
    if (!id) return

    smoothScrollTo(`news-${id}`)
    pendingScrollIdRef.current = null
  }, [pageNews])

  return (
    <div id="notifications" className="mx-auto flex w-full max-w-295 scroll-mt-24 flex-col gap-10 px-4 py-2 text-black">
      <h1 className="text-center text-2xl font-semibold tracking-tight">{title}</h1>

      <div className="flex w-full flex-col gap-8">
        {pageNews.map((item) => (
          <div key={item.id} id={`news-${item.id}`} className="scroll-mt-28">
            <Notification {...item} />
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={goPrev}
            disabled={page === 0}
            className={cn(
              'flex size-9 items-center justify-center rounded-full border border-black/20 transition',
              page === 0 ? 'cursor-not-allowed opacity-30' : 'hover:bg-black/5'
            )}
            aria-label="Previous page"
          >
            <ChevronLeftIcon color="black" className="size-5 stroke-[2]" />
          </button>

          <div className="flex items-center gap-2">
            {paginationDots.map((dot) => (
              <button
                key={dot.id}
                type="button"
                onClick={() => changePage(dot.pageIndex)}
                className={cn(
                  'size-2 rounded-full transition',
                  dot.pageIndex === page ? 'bg-black' : 'bg-black/25 hover:bg-black/45'
                )}
                aria-label={`Go to page ${dot.pageIndex + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            disabled={page === totalPages - 1}
            className={cn(
              'flex size-9 items-center justify-center rounded-full border border-black/20 transition',
              page === totalPages - 1 ? 'cursor-not-allowed opacity-30' : 'hover:bg-black/5'
            )}
            aria-label="Next page"
          >
            <ChevronLeftIcon color="black" className="size-5 rotate-180 stroke-[2]" />
          </button>
        </div>
      )}
    </div>
  )
}
